import { Suspense, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, MessageDescriptor, useIntl } from "react-intl";
import Select, { SingleValue } from "react-select";
import { PricingPlan, useLocalizedPriceDataDropdown } from "src/contentful";
import {
    COUNTRY_CODE_TO_CURRENCY,
    DEFAULT_COUNTRY_CODE,
    LOCALE_CACHE_KEY,
    UserLocale,
    useGeoLocation
} from "src/utils/useGeoCurrency";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";

import Button from "@/components/wpl/button/src";
import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

import { DataCenterProductKey, ProductKey } from "./price-finder/constants";

export const DEFAULT_PRICING_PERIOD = "ANNUAL";

export type CTAWithDataProps = {
    url: string;
    dataEventProps: DataEventProps;
};

interface CalculatorCommonProps {
    buttonWithDataEvent: CTAWithDataProps;
    linkWithDataEvent: CTAWithDataProps;
}

export interface CalculatorSectionProps<P extends ProductKey>
    extends CalculatorCommonProps {
    productKey: DataCenterProductKey<P>;
    serverSidePricing?: PricingPlan[];
    countryCode?: UserLocale;
    messages: CalculatorSectionMessages;
}

export interface CalculatorProps extends CalculatorCommonProps {
    prices: PricingPlan[];
    countryCode: UserLocale;
    messages: Record<string, MessageDescriptor>;
}

export interface CalculatorWithFetchProps<P extends ProductKey>
    extends CalculatorCommonProps {
    productKey: DataCenterProductKey<P>;
    envSource: NodeJS.ProcessEnv | Window;
    messages: Record<string, MessageDescriptor>;
}

export interface CalculatorSectionMessages {
    dataCenter: MessageDescriptor;
    perYear: MessageDescriptor;
    tryItFree: MessageDescriptor;
    buyNow: MessageDescriptor;
    units: MessageDescriptor;
}

const containerStyle = css({
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    backgroundColor: "#f4f5f7",
    padding: "20px 20px 50px 20px",
    textAlign: "center"
});

const dataCenterRedesignHeaderStyle = css({
    padding: "24px 12px",
    h2: {
        margin: 0,
        fontSize: "1.5rem",
        color: "#253858",
        lineHeight: "2rem",
        fontWeight: 700
    },

    [media.above.sm]: {
        h2: {
            fontSize: "2.25rem",
            lineHeight: "2.625rem"
        }
    }
});

const userSelectorStyle = css({
    margin: "32px auto",
    width: "220px",
    height: "42px"
});

const ctaStyle = css({
    // eslint-disable-next-line @atlaskit/design-system/no-physical-properties
    marginBottom: "24px"
});

interface Option {
    readonly value: number;
    readonly label: string;
}

const numberWithCommas = (n: number | string) =>
    n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

const sanitizeUpperLimit = (unitLimitLower: number, unitLimitUpper: number) => {
    return unitLimitUpper ? unitLimitUpper : `${unitLimitLower}+`;
};

export const Calculator = ({
    prices,
    countryCode,
    messages,
    buttonWithDataEvent,
    linkWithDataEvent
}: CalculatorProps) => {
    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [dropdownOptions, setDropdownOptions] = useState<Option[]>();
    const [selected, setSelected] = useState<number>();
    const [currencyCode, setCurrencyCode] = useState<string>();

    useEffect(() => {
        const countryCodeFromSessionStorage = (sessionStorage.getItem(
            LOCALE_CACHE_KEY
        ) || countryCode) as UserLocale;
        const currencyCode =
            COUNTRY_CODE_TO_CURRENCY[countryCodeFromSessionStorage] ||
            COUNTRY_CODE_TO_CURRENCY[DEFAULT_COUNTRY_CODE];
        setCurrencyCode(currencyCode);
        if (countryCodeFromSessionStorage) {
            sessionStorage.setItem(
                LOCALE_CACHE_KEY,
                countryCodeFromSessionStorage
            );
        }
    }, [countryCode]);

    useEffect(() => {
        if (prices) {
            const dropdownOptions = prices.map((price) => ({
                label: `${numberWithCommas(
                    sanitizeUpperLimit(
                        price.unit_limit_lower,
                        price.unit_limit_upper
                    )
                )}  ${intl.formatMessage(
                    messages["units"] as MessageDescriptor
                )}`,
                value: price.prices.find(
                    (item) => item.currency === currencyCode
                )?.unit_price as number
            }));
            setDropdownOptions(dropdownOptions);
            setSelected(dropdownOptions[0]?.value);
        }
    }, [prices, intl, messages, currencyCode]);

    const onChangeHandler = (e: SingleValue<Option>) => {
        setSelected(e?.value);
        createAnalyticsEvent({
            action: "priceBlockDropdownUserSelected",
            event: "priceBlockDropdownChange",
            numberOfUsersSelected: Number(e?.label.replace(/[^\d]/g, "")),
            priceSelected: Number(e?.value),
            currencyCode: currencyCode,
            eventContainer: "priceBlockDropdownLocalized",
            eventComponent: "priceBlockDropdownSelect"
        }).fire();
    };

    return (
        <Suspense>
            <div css={containerStyle}>
                <div css={dataCenterRedesignHeaderStyle}>
                    <h2>
                        {currencyCode} {selected && numberWithCommas(selected)}
                    </h2>
                    <div>
                        <FormattedMessage {...messages["perYear"]} />
                    </div>
                </div>

                <div css={userSelectorStyle}>
                    <Select<Option>
                        options={dropdownOptions}
                        value={dropdownOptions?.filter(function (option) {
                            return option.value === selected;
                        })}
                        onChange={onChangeHandler}
                        aria-label={`${DEFAULT_PRICING_PERIOD} ${
                            messages["units"]?.defaultMessage ?? "users"
                        }`}
                    />
                </div>
                <div css={ctaStyle}>
                    <Button
                        text={intl.formatMessage(
                            messages["tryItFree"] as MessageDescriptor
                        )}
                        aria-label={intl.formatMessage(
                            messages["tryItFree"] as MessageDescriptor
                        )}
                        url={useLocalizedHref(buttonWithDataEvent.url)}
                        dataEventProps={buttonWithDataEvent.dataEventProps}
                    />
                </div>
                <div>
                    <Link
                        text={intl.formatMessage(
                            messages["buyNow"] as MessageDescriptor
                        )}
                        href={useLocalizedHref(linkWithDataEvent.url)}
                        dataEventProps={linkWithDataEvent.dataEventProps}
                    />
                </div>
            </div>
        </Suspense>
    );
};

export const CalculatorSection = <P extends ProductKey>({
    productKey,
    serverSidePricing,
    countryCode,
    buttonWithDataEvent,
    linkWithDataEvent,
    messages
}: CalculatorSectionProps<P>) => {
    const localeMessages = { ...messages } satisfies Record<
        string,
        MessageDescriptor
    >;
    if (serverSidePricing) {
        return (
            <Calculator
                prices={serverSidePricing}
                countryCode={countryCode as UserLocale}
                messages={localeMessages}
                buttonWithDataEvent={buttonWithDataEvent}
                linkWithDataEvent={linkWithDataEvent}
            />
        );
    }
    if (typeof window !== "undefined") {
        return (
            <CalculatorWithFetch
                envSource={window}
                productKey={productKey}
                messages={localeMessages}
                buttonWithDataEvent={buttonWithDataEvent}
                linkWithDataEvent={linkWithDataEvent}
            />
        );
    }

    return (
        <CalculatorWithFetch
            envSource={process.env}
            productKey={productKey}
            messages={localeMessages}
            buttonWithDataEvent={buttonWithDataEvent}
            linkWithDataEvent={linkWithDataEvent}
        />
    );
};

export const CalculatorWithFetch = <P extends ProductKey>({
    productKey,
    envSource,
    messages,
    buttonWithDataEvent,
    linkWithDataEvent
}: CalculatorWithFetchProps<P>) => {
    const { prices, status } = useLocalizedPriceDataDropdown(
        productKey,
        envSource["PRODUCT_CATALOG_DELIVERY_KEY"] as string,
        envSource["PRODUCT_CATALOG_SPACE"] as string
    );

    const { countryCode, status: locationStatus } = useGeoLocation(
        envSource["PRODUCT_CATALOG_LOCATION_BASE_URL"] as string
    );
    if (
        status === "complete" &&
        locationStatus === "complete" &&
        prices &&
        countryCode
    ) {
        return (
            <Calculator
                prices={prices}
                countryCode={countryCode as UserLocale}
                messages={messages}
                buttonWithDataEvent={buttonWithDataEvent}
                linkWithDataEvent={linkWithDataEvent}
            />
        );
    }
    return (
        <Calculator
            prices={prices || []}
            countryCode={countryCode as UserLocale}
            messages={messages}
            buttonWithDataEvent={buttonWithDataEvent}
            linkWithDataEvent={linkWithDataEvent}
        />
    );
};
